import { type RankingItem, type MatchupResult } from '../types';
import { Trophy, Award, RotateCcw, Copy, FileText, FileJson, FileCode } from 'lucide-react';
import { sortByRank, detectTies } from '../utils/ranking';
import { exportToMarkdown, exportToJSON, exportToPlaintext, copyToClipboard } from '../utils/exports';

interface ResultsViewProps {
  items: RankingItem[];
  matchupHistory: MatchupResult[];
  onReset: () => void;
  onTieBreak: (tiedItemIds: string[]) => void;
}

export function ResultsView({ items, matchupHistory, onReset, onTieBreak }: ResultsViewProps) {
  const rankedItems = sortByRank(items);
  const ties = detectTies(items);
  const hasTies = ties.size > 0;

  const handleExport = async (format: 'markdown' | 'json' | 'plaintext' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown':
        content = exportToMarkdown(items, matchupHistory);
        filename = `pairwise-ranking-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = exportToJSON(items, matchupHistory);
        filename = `pairwise-ranking-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'plaintext':
        content = exportToPlaintext(items);
        filename = `pairwise-ranking-${new Date().toISOString().split('T')[0]}.txt`;
        mimeType = 'text/plain';
        break;
      case 'clipboard':
        content = exportToPlaintext(items);
        await copyToClipboard(content);
        alert('Copied to clipboard!');
        return;
    }

    // At this point, format is not 'clipboard' (handled above with early return)
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-6 w-6 text-[var(--mustard)]" />;
    if (rank === 2) return <Award className="h-6 w-6 text-[var(--text-ter)]" />;
    if (rank === 3) return <Award className="h-6 w-6 text-[var(--mustard)]" />;
    return null;
  };

  // Determine display rank (accounting for ties)
  const getDisplayRank = (item: RankingItem, index: number): number => {
    // Find if this item is part of a tie
    for (const [score, itemIds] of ties.entries()) {
      if (item.wins === score && itemIds.includes(item.id)) {
        // Find the first item in this tie group
        const firstTiedIndex = rankedItems.findIndex((i) => i.id === itemIds[0]);
        return firstTiedIndex + 1;
      }
    }
    return index + 1;
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          Final Prioritization
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Your items ranked by preference based on pairwise comparisons.
        </p>

        {/* Ties Warning */}
        {hasTies && (
          <div className="mb-6 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
            <h3 className="font-semibold text-[var(--mustard)] mb-2">Ties Detected</h3>
            <p className="text-sm text-[var(--mustard)] mb-3">
              Some items have the same win count. You can break ties with additional comparisons.
            </p>
            <div className="space-y-2">
              {Array.from(ties.entries()).map(([score, itemIds]) => {
                const tiedNames = itemIds
                  .map((id) => items.find((i) => i.id === id)?.name)
                  .filter(Boolean) as string[];
                return (
                  <div key={score} className="text-sm">
                    <strong>Score {score}:</strong> {tiedNames.join(', ')}
                    <button
                      onClick={() => onTieBreak(itemIds)}
                      className="ml-2 text-[var(--text-ter)] hover:text-[var(--text-ter)] underline"
                    >
                      Break tie
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ranked List */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Ranked List</h3>
          <div className="space-y-3">
            {rankedItems.map((item, index) => {
              const displayRank = getDisplayRank(item, index);
              const isTied = Array.from(ties.values()).some((ids) => ids.includes(item.id));

              return (
                <div
                  key={item.id}
                  className="flex items-center gap-4 p-4 bg-[var(--surface)] border border-[var(--rule)] hover:border-[var(--slate)] transition-colors"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] font-bold text-lg text-[var(--text-sec)]">
                    {getRankIcon(displayRank) || displayRank}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[var(--text-pri)] text-lg">
                      {item.name}
                      {isTied && <span className="text-sm text-[var(--text-ter)] ml-2">(tied)</span>}
                    </h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-sec)]">
                      <span>Wins: <strong>{item.wins}</strong></span>
                      <span>Losses: {item.losses}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Raw Scores Table */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Raw Scores</h3>
          <div className="bg-[var(--surface)] p-4">
            <div className="space-y-2">
              {rankedItems.map((item) => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span className="text-[var(--text-sec)]">{item.name}</span>
                  <span className="font-semibold text-[var(--text-pri)]">{item.wins}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Export Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button
              onClick={() => handleExport('markdown')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span className="text-xs">Markdown</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileJson className="h-5 w-5" />
              <span className="text-xs">JSON</span>
            </button>
            <button
              onClick={() => handleExport('plaintext')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileCode className="h-5 w-5" />
              <span className="text-xs">Plaintext</span>
            </button>
            <button
              onClick={() => handleExport('clipboard')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Copy className="h-5 w-5" />
              <span className="text-xs">Clipboard</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}
